import { useNavigate } from 'react-router-dom';
import { Star, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useFavorites } from '@/hooks/useFavorites';
import { cn } from '@/lib/utils';

export function FavoritesDropdown() {
  const navigate = useNavigate();
  const { favorites, removeFavorite } = useFavorites();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Star className={cn("h-5 w-5", favorites.length > 0 && "fill-amber-400 text-amber-400")} />
          {favorites.length > 0 && (
            <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-primary text-[10px] font-medium text-primary-foreground flex items-center justify-center">
              {favorites.length}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="flex items-center gap-2">
          <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
          Favorite Players ({favorites.length})
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {favorites.length === 0 ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            No favorites yet. Click the star on any player to add them here.
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            {favorites.map((player) => (
              <DropdownMenuItem
                key={player.id}
                className="flex items-center gap-3 cursor-pointer p-2"
                onClick={() => navigate(`/player/${player.id}?from=stats`)}
              >
                <img
                  src={player.photo}
                  alt={player.name}
                  className="h-8 w-8 rounded-full object-cover bg-muted"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/placeholder.svg';
                  }}
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{player.name}</p>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <img
                      src={player.teamLogo}
                      alt={player.teamName}
                      className="h-3 w-3 object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/placeholder.svg';
                      }}
                    />
                    <span className="truncate">{player.teamName}</span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0 hover:bg-destructive/10 hover:text-destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFavorite(player.id);
                  }}
                >
                  <X className="h-3 w-3" />
                </Button>
              </DropdownMenuItem>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
